import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  getRepositoriesQueryOptions,
  getRepositoryScorecardsQueryOptions,
  getRepositoryThreatModelContentOptions,
  getRepositoryThreatModelQueryOptions,
  useGetRepositories,
} from '../../utils/api-client/api-hooks.ts'
import { type EstateFreshness, summariseFreshness } from './estate-freshness.ts'
import { aggregateOrgScorecards, type OrgScorecardPosture } from './org-scorecard-posture.ts'
import { aggregateOrgPosture, type OrgThreatPosture, type RepositoryPosture } from './org-threat-posture.ts'

/**
 * Fans the per-repository threat model out across the estate, because there is no org-level endpoint.
 *
 * Every threat-model route is scoped to one `repositoryId`, so painting one org page costs one request
 * per page of the repository list plus one per repository. That is the cost this view exists to make
 * visible: it is fine on a handful of repositories and plainly wrong at a few hundred, which is the
 * evidence for asking analysis-service for an aggregate.
 *
 * **The estate is no longer capped at one page**, so that cost is now paid in full — roughly 900 requests
 * on 250 repositories. Capping it was worse: it made the numbers wrong rather than slow, and a security
 * posture measured over an arbitrary 100 of 250 repositories is not a security posture. Paying the cost
 * honestly is also what turns the aggregate-endpoint ask from a hypothetical into a measurement.
 *
 * Partial results are returned as they arrive rather than held for the slowest repository. A repository
 * still in flight is simply not modelled yet from the aggregate's point of view, and the figures move
 * as it lands — the alternative is a blank page for as long as the slowest request takes.
 *
 * **Three fan-outs, and only two of them are per-repository unconditionally.** Posture comes from each
 * model's `/content`, freshness from metadata on the model itself, and the scorecard roll-up from
 * `/scorecards` — no endpoint returns any two together. Scorecards are asked only of repositories that
 * have a model, since a scorecard cannot exist without one, so the list costs 1 + 2N + M where M is the
 * modelled count. On an estate early in adoption M is far below N; on a fully-modelled one it is 1 + 3N.
 * Either way this makes the case for an org-level aggregate endpoint louder rather than weaker.
 */

/**
 * How many repositories each list request asks for. Not a limit on the estate — every page is read.
 *
 * This view used to stop after one page, which meant an organization of 250 repositories silently got a
 * security posture measured over 100 of them. A figure that is quietly wrong about the size of the estate
 * is worse than a slow one, so the cap is gone and the cost is paid honestly.
 */
const REPOSITORY_PAGE_SIZE = 100

export type UseOrgThreatPostureResult = {
  posture: OrgThreatPosture
  /** From a second fan-out over the model metadata; a sibling of `posture`, not part of it. */
  freshness: EstateFreshness
  /** From a third fan-out over the scorecards; a sibling again, and from a different endpoint. */
  scorecardPosture: OrgScorecardPosture
  isLoadingRepositories: boolean
  /** True while any repository's model is still outstanding, so the page can say the figures will move. */
  isAggregating: boolean
  /** How many repositories have answered, for an honest progress line. */
  settledCount: number
  requestCount: number
  /**
   * Repositories the estate knows exist but could not read — a page of the list that failed.
   *
   * No longer a cap: every page is fetched, so this is zero in the healthy case and non-zero only when a
   * request failed. It stays exposed because a partial estate silently presented as a whole one is the
   * failure mode worth guarding, however it arises.
   */
  unreadRepositoryCount: number
}

export const useOrgThreatPosture = (): UseOrgThreatPostureResult => {
  const { data: firstPage, isLoading: isLoadingRepositories } = useGetRepositories({
    pageIndex: 0,
    pageSize: REPOSITORY_PAGE_SIZE,
  })

  /**
   * The estate's true size, from the first page's own `total` — so the rest of the list can be fetched and
   * the progress line can name a denominator that does not grow as pages land.
   */
  const repositoryCount = firstPage?.total ?? 0

  /**
   * Pages two onward. Their count comes from `total`, so this fans out once the first page answers rather
   * than walking the list one page at a time — the pages are independent and nothing is gained by
   * serialising them.
   */
  const laterPageIndexes = useMemo(() => {
    const pageCount = Math.ceil(repositoryCount / REPOSITORY_PAGE_SIZE)
    return Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) => index + 1)
  }, [repositoryCount])

  const { laterPageItems, areLaterPagesSettled } = useQueries({
    queries: laterPageIndexes.map(pageIndex =>
      getRepositoriesQueryOptions({ pageIndex, pageSize: REPOSITORY_PAGE_SIZE })
    ),
    combine: results => ({
      laterPageItems: results.flatMap(result => result.data?._embedded?.items ?? []),
      areLaterPagesSettled: results.every(result => !result.isPending),
    }),
  })

  const repositories = useMemo(
    () => [...(firstPage?._embedded?.items ?? []), ...laterPageItems],
    [firstPage, laterPageItems]
  )

  /**
   * `combine` rather than reading the array in a `useMemo`: the array `useQueries` returns is a fresh
   * object every render, so a memo keyed on it would recompute the whole aggregate on every render for
   * every repository. `combine` runs only when a result actually changes.
   */
  const { entries, settledCount } = useQueries({
    queries: repositories.map(repository => ({
      ...getRepositoryThreatModelContentOptions({ repositoryId: repository.id }),
      // A repository with no model answers 404. That is an expected answer here, not a failure worth
      // retrying once per repository across the whole estate.
      retry: false,
    })),
    combine: results => ({
      // Typed through `map`'s generic rather than by annotating the callback: an annotated arrow in
      // this position is formatted differently by Prettier 3.6 and 3.9, so the two disagree forever.
      entries: repositories.map<RepositoryPosture>((repository, index) => ({
        repositoryId: repository.id,
        name: repository.name,
        // `undefined` covers both "no model generated" and "not back yet". The aggregate treats the
        // repository as unmodelled either way; `isAggregating` is what tells the reader which it is.
        content: results[index]?.data,
      })),
      settledCount: results.filter(result => !result.isPending).length,
    }),
  })

  /**
   * The clock is read once per render pass rather than inside the bucketing, so every model in one
   * pass is aged against the same instant and a band cannot shift mid-aggregate.
   */
  const { freshnessInputs } = useQueries({
    queries: repositories.map(repository => ({
      ...getRepositoryThreatModelQueryOptions({ repositoryId: repository.id }),
      // The shared options poll every 10s while a generation is in flight. Correct for one repository's
      // page; across an estate it would leave this page polling once per repository for as long as it is
      // open. See `getRepositoryThreatModelQueryOptions`.
      refetchInterval: false as const,
      retry: false,
    })),
    combine: results => ({
      freshnessInputs: repositories.flatMap((repository, index) => {
        const model = results[index]?.data
        // `null` is a 404 — no model has ever been generated. That repository belongs to `withoutModel`
        // in the posture aggregate, not to a freshness band, so it is dropped here rather than counted
        // as undated.
        return model ? [{ repositoryId: repository.id, generatedAt: model.generatedAt }] : []
      }),
    }),
  })

  /**
   * Which repositories have a model at all — the metadata fan-out already answers this by dropping the
   * 404s, so the gate below costs nothing extra to know.
   */
  const modelledRepositoryIds = useMemo(
    () => new Set(freshnessInputs.map(input => input.repositoryId)),
    [freshnessInputs]
  )

  const { scorecardInputs } = useQueries({
    queries: repositories.map(repository => ({
      ...getRepositoryScorecardsQueryOptions({ repositoryId: repository.id }),
      // A scorecard cannot exist without a threat model, so asking an unmodelled repository for one is a
      // question with no possible answer. Gated per repository rather than behind a barrier: each request
      // goes out as soon as its own model resolves, so nothing waits on the slowest.
      enabled: modelledRepositoryIds.has(repository.id),
      // These options poll while a scorecard is `processing`. See
      // `getRepositoryScorecardsQueryOptions` — across an estate that would never stop.
      refetchInterval: false as const,
      retry: false,
    })),
    combine: results => ({
      scorecardInputs: repositories.flatMap((repository, index) => {
        // `scorecard` is absent until the run completes, so `processing` and `failed` contribute nothing
        // rather than contributing a zero.
        const payload = results[index]?.data?.scorecard
        return payload ? [{ repositoryId: repository.id, name: repository.name, scorecard: payload }] : []
      }),
    }),
  })

  const posture = useMemo(() => aggregateOrgPosture(entries), [entries])
  const scorecardPosture = useMemo(() => aggregateOrgScorecards(scorecardInputs), [scorecardInputs])
  const freshness = useMemo(
    () => summariseFreshness({ models: freshnessInputs, now: new Date().toISOString() }),
    [freshnessInputs]
  )

  return {
    posture,
    freshness,
    scorecardPosture,
    isLoadingRepositories,
    // Measured against the fan-out's own denominator, not the API's declared total: one content query is
    // issued per entry in `repositories`, so `settledCount` can never reach a `repositoryCount` inflated
    // by a page that failed to load — which left this true forever, with the progress line on screen for
    // good. Pages still in flight are the first clause's job, and `unreadRepositoryCount` is what tells
    // the reader some repositories could not be read.
    isAggregating: !areLaterPagesSettled || settledCount < repositories.length,
    settledCount,
    requestCount: Math.max(repositoryCount, repositories.length),
    // Only meaningful once the pages have settled; before that the shortfall is just work in flight.
    unreadRepositoryCount: areLaterPagesSettled ? Math.max(0, repositoryCount - repositories.length) : 0,
  }
}
